// SPDX-License-Identifier: AGPL-3.0-or-later

import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import type {NativeScreenCaptureAvailability, NativeScreenCaptureFrameMessage} from '@app/types/electron.d';

export const MAX_GENERATOR_PENDING_VIDEO_FRAMES = 2;
export const MIN_NATIVE_SCREEN_DIM = 16;
export const MAX_NATIVE_SCREEN_DIM = 8192;

export interface VideoFrameInit {
	format: 'NV12' | 'BGRA' | 'I420' | string;
	codedWidth: number;
	codedHeight: number;
	displayWidth?: number;
	displayHeight?: number;
	timestamp: number;
	layout?: Array<{offset: number; stride: number}>;
}

export interface CanvasVideoFrameInit {
	timestamp: number;
	displayWidth?: number;
	displayHeight?: number;
}

export interface RendererVideoFrame {
	close: () => void;
}

export interface VideoFrameCtor {
	new (data: BufferSource, init: VideoFrameInit): RendererVideoFrame;
	new (data: CanvasImageSource, init: CanvasVideoFrameInit): RendererVideoFrame;
}

export interface GeneratorVideoTrack extends MediaStreamTrack {
	writable: WritableStream<unknown>;
}

export interface GeneratorVideoCtor {
	new (options: {kind: 'video'}): GeneratorVideoTrack;
}

export interface NativeScreenBridgeHandle {
	track: MediaStreamTrack;
	cleanup: (stopRemote?: boolean) => Promise<void>;
}

export interface NativeScreenBridgeBootstrap {
	initialFrame?: NativeScreenCaptureFrameMessage;
	fallbackWidth?: number;
	fallbackHeight?: number;
	fallbackThumbnailDataUrl?: string;
}

export function getVideoFrameCtor(): VideoFrameCtor | undefined {
	return (
		window as typeof window & {
			VideoFrame?: VideoFrameCtor;
		}
	).VideoFrame;
}

export function getGeneratorVideoCtor(): GeneratorVideoCtor | undefined {
	return (
		window as typeof window & {
			MediaStreamTrackGenerator?: GeneratorVideoCtor;
		}
	).MediaStreamTrackGenerator;
}

export function getNativeScreenCaptureApi() {
	return getElectronAPI()?.nativeScreenCapture ?? null;
}

export function isArrayBuffer(value: unknown): value is ArrayBuffer {
	return value instanceof ArrayBuffer || Object.prototype.toString.call(value) === '[object ArrayBuffer]';
}

export function normalizeNativeScreenCaptureDimension(value: number | undefined): number | undefined {
	if (value === undefined || value <= 0 || !Number.isFinite(value)) return undefined;
	let normalized = Math.max(MIN_NATIVE_SCREEN_DIM, Math.min(MAX_NATIVE_SCREEN_DIM, Math.floor(value)));
	if (normalized % 2 !== 0) {
		normalized = normalized < MAX_NATIVE_SCREEN_DIM ? normalized + 1 : normalized - 1;
	}
	return normalized;
}

export function normalizeNativeScreenCaptureResolution(
	resolution:
		| {
				width?: number;
				height?: number;
		  }
		| undefined,
):
	| {
			width?: number;
			height?: number;
	  }
	| undefined {
	const width = normalizeNativeScreenCaptureDimension(resolution?.width);
	const height = normalizeNativeScreenCaptureDimension(resolution?.height);
	if (width === undefined && height === undefined) return undefined;
	return {width, height};
}

export function isValidVideoFrameMessage(message: NativeScreenCaptureFrameMessage): boolean {
	if (!message || typeof message !== 'object') return false;
	if (message.format !== 'nv12' && message.format !== 'bgra') return false;
	if (
		typeof message.width !== 'number' ||
		!Number.isSafeInteger(message.width) ||
		message.width < MIN_NATIVE_SCREEN_DIM ||
		message.width > MAX_NATIVE_SCREEN_DIM
	) {
		return false;
	}
	if (
		typeof message.height !== 'number' ||
		!Number.isSafeInteger(message.height) ||
		message.height < MIN_NATIVE_SCREEN_DIM ||
		message.height > MAX_NATIVE_SCREEN_DIM
	) {
		return false;
	}
	if (message.format === 'nv12' && (message.width % 2 !== 0 || message.height % 2 !== 0)) return false;
	if (typeof message.strideY !== 'number' || !Number.isSafeInteger(message.strideY)) return false;
	if (typeof message.strideUV !== 'number' || !Number.isSafeInteger(message.strideUV)) return false;
	const minStride = message.format === 'nv12' ? message.width : message.width * 4;
	if (message.strideY < minStride || message.strideUV < minStride) return false;
	if (!Number.isFinite(message.timestampUs) || message.timestampUs < 0) return false;
	if (!isArrayBuffer(message.data)) return false;
	const minBytes =
		message.format === 'nv12'
			? message.strideY * message.height + message.strideUV * (message.height / 2)
			: message.strideY * message.height;
	return message.data.byteLength >= minBytes;
}

export function unsupportedPlatformScreenAvailability(): NativeScreenCaptureAvailability {
	return {available: false, reason: 'unsupported-platform'};
}

export function nv12VideoFrameLayout(
	width: number,
	height: number,
	strideY?: number,
	strideUV?: number,
): Array<{offset: number; stride: number}> {
	const effectiveStrideY = strideY != null && strideY >= width ? strideY : width;
	const effectiveStrideUV = strideUV != null && strideUV >= width ? strideUV : width;
	const yPlaneBytes = effectiveStrideY * height;
	return [
		{offset: 0, stride: effectiveStrideY},
		{offset: yPlaneBytes, stride: effectiveStrideUV},
	];
}
